import { ReactNode, useState } from "react";
import { Icon } from "@juiceforward/narrative-player";

interface AdminLayoutProps {
  children: ReactNode;
  currentView: string;
  onViewChange: (view: string) => void;
  onNewNarrativeClick: () => void;
  onAddUserClick?: () => void;
  onLogout: () => void;
  isDrilledDown?: boolean;
}

export const AdminLayout = ({
  children,
  currentView,
  onViewChange,
  onNewNarrativeClick,
  onAddUserClick,
  onLogout,
  isDrilledDown = false,
}: AdminLayoutProps) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const navItems = [
    { id: "narratives", label: "Narratives", icon: "book" },
    { id: "users", label: "Users", icon: "users" },
    { id: "analytics", label: "Analytics", icon: "chart-simple" },
    { id: "settings", label: "Settings", icon: "cog" },
  ];

  return (
    <div className="flex h-screen bg-[var(--admin-bg)] text-[var(--admin-text)] font-sans">
      {/* Sidebar */}
      <aside
        className={`${
          isSidebarCollapsed ? "w-20" : "w-64"
        } bg-[var(--admin-surface)] border-r border-[var(--admin-border)] transition-all duration-300 flex flex-col`}
      >
        {/* Header */}
        <div className="p-6 border-b border-[var(--admin-border)] flex items-center justify-between">
          {!isSidebarCollapsed && (
            <div className="flex items-center gap-3">
              <div className="text-2xl font-bold text-emerald-400">
                /Forward
              </div>
            </div>
          )}
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className="text-[var(--admin-text-muted)] hover:text-[var(--admin-text)] transition-colors"
            title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <Icon icon={isSidebarCollapsed ? "angles-right" : "angles-left"} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {navItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => onViewChange(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    currentView === item.id
                      ? "bg-emerald-500/20 text-emerald-400"
                      : "text-[var(--admin-text-muted)] hover:bg-[var(--admin-surface-hover)] hover:text-[var(--admin-text)]"
                  }`}
                  title={isSidebarCollapsed ? item.label : undefined}
                >
                  <Icon icon={item.icon} className="text-lg" />
                  {!isSidebarCollapsed && <span>{item.label}</span>}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* User info */}
        <div className="p-4 border-t border-[var(--admin-border)]">
          <div
            className={`flex items-center gap-3 ${isSidebarCollapsed ? "justify-center" : ""}`}
          >
            <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
              <Icon icon="user" className="text-emerald-400" />
            </div>
            {!isSidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">Admin User</div>
                <div className="text-xs text-[var(--admin-text-subtle)]">
                  admin@forward.ai
                </div>
              </div>
            )}
          </div>
          {!isSidebarCollapsed && (
            <button
              onClick={onLogout}
              className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-[var(--admin-text-muted)] hover:text-[var(--admin-text)] hover:bg-[var(--admin-surface-hover)] rounded-lg transition-colors"
            >
              <Icon icon="arrow-right-from-bracket" />
              <span>Logout</span>
            </button>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-16 bg-[var(--admin-surface)] border-b border-[var(--admin-border)] px-6 flex items-center justify-between">
          <h1 className="text-xl font-semibold capitalize">{currentView}</h1>
          <div className="flex items-center gap-4">
            {currentView === "narratives" && !isDrilledDown && (
              <button
                onClick={onNewNarrativeClick}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors text-sm font-medium text-white"
              >
                <Icon icon="plus" className="mr-2" />
                New Narrative
              </button>
            )}
            {currentView === "users" && (
              <button
                onClick={onAddUserClick}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors text-sm font-medium text-white"
              >
                <Icon icon="plus" className="mr-2" />
                Add User
              </button>
            )}
          </div>
        </header>

        {/* Content area */}
        <div className="flex-1 overflow-auto p-6">{children}</div>
      </main>
    </div>
  );
};
